import React, { useEffect, useState } from 'react';
import {
  BarChart3,
  Activity,
  Gauge,
  CalendarRange,
  Layers,
  PieChart,
  Cpu,
  ArrowUpRight,
  LineChart,
  Globe,
  TrendingUp,
  Users,
  Settings,
  Sparkles,
  CheckCircle,
} from 'lucide-react';
import MainNavbar from '../../components/layout/MainNavbar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const kpiHighlights = [
  {
    title: 'Fleet Uptime',
    value: '99.3%',
    delta: '+1.8%',
    context: 'Achieved with predictive maintenance alerts and route balancing.',
  },
  {
    title: 'On-Time Deliveries',
    value: '96%',
    delta: '+3.4%',
    context: 'Powered by AI-driven ETA recalculations and customer notifications.',
  },
  {
    title: 'Fuel Spend Reduction',
    value: '28%',
    delta: '-12%',
    context: 'Optimised idling, load planning, and eco-driving interventions.',
  },
];

const analyticsModules = [
  {
    icon: BarChart3,
    title: 'Operations Intelligence',
    description:
      'Drill down into routes, vehicles, and hubs with multi-dimensional reports, configurable dashboards, and automated weekly digests.',
    image:
      'https://images.unsplash.com/photo-1517433456452-f9633a875f6f?w=1200&auto=format&fit=crop&q=60',
  },
  {
    icon: PieChart,
    title: 'Financial Analytics',
    description:
      'Track cost per kilometre, expense variance, and revenue attribution with ledger integrations and anomaly detection for fraudulent spends.',
    image:
      'https://images.unsplash.com/photo-1483478550801-ceba5fe50e8e?w=1200&auto=format&fit=crop&q=60',
  },
  {
    icon: Gauge,
    title: 'Driver Performance',
    description:
      'Scorecards, heatmaps, and coaching workflows identify risky behaviour and recognise top performers across regions and fleet types.',
    image:
      'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=1200&auto=format&fit=crop&q=60',
  },
];

const insightPillars = [
  {
    title: 'Predictive Forecasting',
    description:
      'Model demand surges, seasonal drops, and maintenance windows with machine learning that ingests traffic, weather, and utilisation data.',
    points: [
      'Capacity planning recommendations with exportable schedules',
      'What-if simulations for fleet expansion or consolidation scenarios',
      'Alerts when predicted utilisation breaches thresholds',
    ],
    icon: Cpu,
  },
  {
    title: 'Customer Experience Lens',
    description:
      'Correlate delivery punctuality, SLA compliance, and support tickets to understand customer impact and prioritise actions that move NPS.',
    points: [
      'Unified view of ticketing, delivery, and feedback platforms',
      'Geo-segmentation of service issues to target fixes faster',
      'Churn propensity scoring and retention campaign triggers',
    ],
    icon: Users,
  },
  {
    title: 'Sustainability & Compliance',
    description:
      'Measure emissions, route efficiency, and driver behaviour to meet ESG goals, DPDP, and AIS-140 standards with auditable evidence.',
    points: [
      'Automated ESG reports with emissions intensity benchmarks',
      'Digital documentation vault with immutable audit trails',
      'Compliance dashboards with proactive remediation actions',
    ],
    icon: Settings,
  },
];

const AnalyticsPage = () => {
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);
    document.documentElement.classList.toggle('dark', savedTheme === 'dark');
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  return (
    <div
      className={`min-h-screen relative overflow-hidden transition-colors duration-500 border-1 border-muted-foreground/20 ${
        theme === 'dark'
          ? 'bg-gradient-to-r from-[#0b1120] via-[#1a1f37] to-[#2d1b4e] text-slate-100'
          : 'bg-gradient-to-br from-slate-50 via-indigo-50 to-purple-50 text-slate-900'
      }`}
    >
      <MainNavbar theme={theme} toggleTheme={toggleTheme} activePage="Analytics" />

      <div className="absolute inset-0 pointer-events-none">
        {theme === 'dark' ? (
          <>
            <div className="absolute -top-24 -left-24 w-96 h-96 bg-indigo-500/30 rounded-full blur-3xl animate-pulse" />
            <div className="absolute top-1/3 right-10 w-80 h-80 bg-purple-500/20 rounded-full blur-[160px] animate-pulse delay-200" />
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[30rem] h-[30rem] bg-blue-500/20 rounded-full blur-[200px] animate-pulse delay-500" />
          </>
        ) : (
          <>
            <div className="absolute -top-24 -left-24 w-96 h-96 bg-blue-200/40 rounded-full blur-3xl animate-pulse" />
            <div className="absolute top-1/2 right-0 w-80 h-80 bg-purple-200/40 rounded-full blur-3xl animate-pulse delay-200" />
            <div className="absolute bottom-0 left-1/3 w-[30rem] h-[30rem] bg-indigo-200/40 rounded-full blur-3xl animate-pulse delay-500" />
          </>
        )}
      </div>

      <main className="relative z-10 pt-28 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto space-y-12">
          <header className="space-y-5 text-center">
            <div
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-full ${
                theme === 'dark'
                  ? 'bg-slate-900 border-1 border-muted-foreground/20 text-blue-200'
                  : 'bg-white border-1 border-muted-foreground/20 text-blue-700'
              }`}
            >
              <BarChart3 className="h-4 w-4" />
              Unified Analytics Hub
            </div>
            <h1 className="text-sm sm:text-4xl md:text-5xl font-bold leading-tight">
              Transform Fleet Data into Predictive, Actionable Intelligence
            </h1>
            <p
              className={`text-sm sm:text-lg md:text-xl max-w-3xl mx-auto ${
                theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
              }`}
            >
              TrackYourWheelz Analytics connects telematics, operations, finance, and customer
              systems to surface insights that improve margins, service quality, and sustainability.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
             
            </div>
          </header>

          <section className="grid gap-6 md:grid-cols-3">
            {kpiHighlights.map((kpi) => (
              <Card
                key={kpi.title}
                className={`border-1 border-muted-foreground/20 ${
                  theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'
                } shadow-lg`}
              >
                <CardHeader className="space-y-2">
                  <CardTitle className="text-sm sm:text-base font-semibold">{kpi.title}</CardTitle>
                  <CardDescription
                    className={`text-3xl sm:text-4xl font-bold ${
                      theme === 'dark' ? 'text-blue-200' : 'text-blue-600'
                    }`}
                  >
                    {kpi.value}
                  </CardDescription>
                  <span
                    className={`inline-flex items-center gap-1 text-xs font-semibold ${
                      kpi.delta.startsWith('-')
                        ? 'text-rose-500'
                        : theme === 'dark'
                        ? 'text-emerald-300'
                        : 'text-emerald-600'
                    }`}
                  >
                    <TrendingUp className="h-3 w-3" />
                    {kpi.delta}
                  </span>
                </CardHeader>
                <CardContent
                  className={`text-sm leading-relaxed ${
                    theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
                  }`}
                >
                  {kpi.context}
                </CardContent>
              </Card>
            ))}
          </section>

          <section className="space-y-6">
            <h2 className="text-sm sm:text-3xl font-bold text-center">Analytics Control Tower</h2>
            <p
              className={`text-sm sm:text-base max-w-3xl mx-auto text-center ${
                theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
              }`}
            >
              Visualise trends, compare cohorts, and design automations across every dimension of
              your fleet with ready-to-use dashboards.
            </p>
            <div className="grid gap-6 lg:grid-cols-3">
              {analyticsModules.map((module) => {
                const Icon = module.icon;
                return (
                  <Card
                    key={module.title}
                    className={`overflow-hidden border-1 border-muted-foreground/20 ${
                      theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'
                    } shadow-lg`}
                  >
                    <div className="relative h-40">
                      <img
                        src={module.image}
                        alt={module.title}
                        className="absolute inset-0 h-full w-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.src =
                            'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=1200&auto=format&fit=crop&q=60';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/40 to-transparent" />
                      <div className="absolute bottom-3 left-3 flex items-center gap-2 text-white">
                        <Icon className="h-5 w-5" />
                        <span className="text-sm font-semibold">{module.title}</span>
                      </div>
                    </div>
                    <CardContent
                      className={`p-5 text-sm leading-relaxed ${
                        theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
                      }`}
                    >
                      {module.description}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          <section className="space-y-8">
            <h2 className="text-sm sm:text-3xl font-bold text-center">Insight Pillars</h2>
            <div className="space-y-6">
              {insightPillars.map((pillar) => {
                const Icon = pillar.icon;
                return (
                  <Card
                    key={pillar.title}
                    className={`border-1 border-muted-foreground/20 ${
                      theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'
                    } shadow-lg`}
                  >
                    <CardHeader className="flex flex-col gap-3 md:flex-row md:items-start md:gap-6">
                      <div
                        className={`h-11 w-11 rounded-xl flex items-center justify-center ${
                          theme === 'dark'
                            ? 'bg-blue-500/20 text-blue-200'
                            : 'bg-blue-100 text-blue-700'
                        }`}
                      >
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="space-y-2">
                        <CardTitle className="text-sm sm:text-lg font-semibold">
                          {pillar.title}
                        </CardTitle>
                        <CardDescription
                          className={`text-sm ${
                            theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
                          }`}
                        >
                          {pillar.description}
                        </CardDescription>
                      </div>
                    </CardHeader>
                    <CardContent className="pb-6">
                      <ul
                        className={`grid gap-3 sm:grid-cols-2 text-sm ${
                          theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
                        }`}
                      >
                        {pillar.points.map((point) => (
                          <li key={point} className="flex items-start gap-2">
                            <CheckCircle className="mt-1 h-4 w-4 text-blue-500 flex-shrink-0" />
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          <section className="grid gap-6 lg:grid-cols-2">
            <Card
              className={`border-1 border-muted-foreground/20 ${
                theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'
              } shadow-lg`}
            >
              <CardHeader>
                <CardTitle className="text-sm sm:text-lg font-semibold flex items-center gap-2">
                  <LineChart className="h-5 w-5 text-blue-500" />
                  Live Storyboards
                </CardTitle>
                <CardDescription
                  className={`text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}
                >
                  Collaborative storyboards combine charts, commentary, and action items so leaders
                  can align on priorities in real time.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <p>
                  Build narratives with data snapshots, annotate anomalies, and broadcast updates to
                  operations, finance, or customer teams with one click.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <Sparkles className="mt-1 h-4 w-4 text-blue-500" />
                    AI summarisation produces meeting-ready insight decks
                  </li>
                  <li className="flex items-start gap-2">
                    <CalendarRange className="mt-1 h-4 w-4 text-blue-500" />
                    Scheduled snapshots ensure stakeholders never miss updates
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card
              className={`border-1 border-muted-foreground/20 ${
                theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'
              } shadow-lg`}
            >
              <CardHeader>
                <CardTitle className="text-sm sm:text-lg font-semibold flex items-center gap-2">
                  <Layers className="h-5 w-5 text-blue-500" />
                  Unified Data Fabric
                </CardTitle>
                <CardDescription
                  className={`text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}
                >
                  Seamless connectors bring together telematics, ERP, CRM, HRMS, and IoT systems into
                  a gold-standard analytics warehouse.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <p>
                  Orchestrate data pipelines with automatic schema mapping, governance controls, and
                  real-time streaming for mission-critical dashboards.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <Globe className="mt-1 h-4 w-4 text-blue-500" />
                    Open APIs and prebuilt connectors for SAP, Oracle, and Snowflake
                  </li>
                  <li className="flex items-start gap-2">
                    <Activity className="mt-1 h-4 w-4 text-blue-500" />
                    Built-in observability for data freshness and pipeline health
                  </li>
                </ul>
              </CardContent>
            </Card>
          </section>

          <section className="rounded-3xl overflow-hidden relative">
            <img
              src="https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=1600&auto=format&fit=crop&q=60"
              alt="Analytics war room"
              className="h-72 sm:h-80 w-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
            <div className="absolute inset-y-0 left-0 px-6 sm:px-10 py-8 sm:py-10 flex flex-col justify-between text-white max-w-xl">
              <div className="space-y-3">
                <h2 className="text-sm sm:text-3xl font-bold">Analytics for every stakeholder</h2>
                <p className="text-sm sm:text-base opacity-80">
                  Role-based workspaces ensure operations, finance, customer success, and leadership
                  get tailored dashboards and actions that move their goals.
                </p>
              </div>
              <div className="flex flex-wrap gap-3 text-xs sm:text-sm">
                <span className="rounded-full border border-white/40 px-4 py-1">Self-serve BI</span>
                <span className="rounded-full border border-white/40 px-4 py-1">Embedded analytics</span>
                <span className="rounded-full border border-white/40 px-4 py-1">Data governance</span>
              </div>
            </div>
          </section>

          <footer
            className={`rounded-2xl border-1 border-muted-foreground/20 p-6 sm:p-8 text-center ${
              theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'
            }`}
          >
            <h2 className="text-sm sm:text-3xl font-bold mb-4">Ready to accelerate decisions?</h2>
            <p
              className={`text-sm sm:text-base max-w-2xl mx-auto mb-6 ${
                theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
              }`}
            >
              TrackYourWheelz Analytics is trusted by enterprises across logistics, mobility, public
              transport, and last-mile delivery to drive tangible ROI from their data estate.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                onClick={() => window.open('https://trackyourwheelz.com/analytics', '_blank')}
                className={`h-11 px-6 text-sm font-semibold text-white shadow-md ${
                  theme === 'dark'
                    ? 'bg-gradient-to-r from-[#483D8B] to-[#2d1b4e] hover:from-[#5a4da3] hover:to-[#3d2f6e]'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                }`}
              >
                Start with insights
              </Button>
              <Button
                variant="outline"
                onClick={() => window.open('mailto:analytics@trackyourwheelz.com', '_blank')}
                className={`h-11 px-6 text-sm font-semibold ${
                  theme === 'dark'
                    ? 'border-white/30 text-white hover:bg-white/10'
                    : 'border-blue-200 text-blue-700 hover:bg-blue-50'
                }`}
              >
                Request a workshop
              </Button>
            </div>
          </footer>
        </div>
      </main>
    </div>
  );
};

export default AnalyticsPage;